import React, { useEffect } from 'react';
import { FormField, Textarea } from '@amzn/awsui-components-console';
import { Controller, useFormContext } from 'react-hook-form';
import { get } from 'lodash-es';
import { DiffContent, ViewContent, useEditOnChange, useIsEditContentDisabled } from './contentHelpers';
import { DisplayMode, ResourcePath, ResourceType, iCodeContent } from './contentInterfaces';
import { LabeledContentSkeleton } from '../skeletons/LabeledContentSkeleton';
import { BetterCodeEditor, CodeTextBox, HelpPanelInfoLink } from '..';
import { DataAttributes, filterDataAttributes, getContentDataAttributes } from 'src/commons/dataAttributes';
import { ContentErrorWrapper } from 'src/components/error/ContentErrorBoundary';
import { Rules } from '.';
import { getNodeText } from 'src/commons';

/** Renders the field identified by the `path` as a Polaris `<CodeEditor>` component. Manipulates the value as a `string` type
 * For more information on `<Content>` components, see the README.md in the `src/components/helpers/content/` directory. */
export const CodeContent = ContentErrorWrapper(_CodeContent);

function _CodeContent<RType extends keyof ResourceType, DisabledOnPaths extends ResourcePath<RType>[]>(
	props: iCodeContent<RType, DisabledOnPaths>
) {
	// Processed at top level component to avoid unneccessary executions of a relatively heavy functions between renders
	const dataAttributes = { ...filterDataAttributes(props), ...getContentDataAttributes('Code', props) };
	// NOTE: CodeTextBox should be replaced with with CloudScape `CodeView` once npmpm updates are merged
	const viewTransform = (value: string) => (
		<CodeTextBox language={props.language}>{props.viewTransform?.(value) ?? value}</CodeTextBox>
	);

	switch (props.mode) {
		case DisplayMode.Diff:
			// NOTE: This will not yet show line-by-line diffs between code blocks -- that is a more complex feature
			// which we can implement later as a custom DiffCodeContent component
			return <DiffContent {...props} dataAttributes={dataAttributes} viewTransform={viewTransform} />;
		case DisplayMode.View:
			return <ViewContent {...props} dataAttributes={dataAttributes} viewTransform={viewTransform} />;
		case DisplayMode.Edit:
			return <EditCodeContent {...props} dataAttributes={dataAttributes} />;
		case DisplayMode.Loading:
			return <LabeledContentSkeleton label={props.label} height={props.editorContentHeight ?? '7em'} />;
		default:
			return null;
	}
}

function EditCodeContent<RType extends keyof ResourceType, DisabledOnPaths extends ResourcePath<RType>[]>(
	props: iCodeContent<RType, DisabledOnPaths> & { dataAttributes: DataAttributes }
) {
	const { control, clearErrors, setValue } = useFormContext<ResourceType[RType]>();
	const disabled = useIsEditContentDisabled(props);

	return (
		<Controller
			control={control}
			name={props.path}
			rules={Rules.useRules(props.rules, props.label, disabled)}
			render={({ field, formState, fieldState }) => {
				const onChange = useEditOnChange(props, field.onChange, setValue, 'value');

				useEffect(() => {
					// This effect will ignore any potential validation errors if the particular field is disabled
					if (disabled) {
						clearErrors(props.path);
					}
				}, [disabled, formState.isSubmitting, formState.isValidating]);

				let CodeEditor: JSX.Element;

				try {
					// Tries to load a BetterCodeEditor component. If ace fails to load or there is some other internal error, defaults to a Textarea component
					CodeEditor = (
						<BetterCodeEditor
							data-testid={getNodeText(props.label)}
							language={props.language}
							value={field.value as string}
							onChange={onChange}
							disabled={disabled}
							editorContentHeight={props.editorContentHeight}
							// Validation is provided by the Ace syntax checker, but we will also trigger the `react-hook-form` event to check for custom rules
							onValidate={field.onBlur}
						/>
					);
				} catch (error) {
					console.error('Failed to load CodeEditor in BetterCodeEditor', error);
					CodeEditor = (
						<Textarea
							data-testid={getNodeText(props.label)}
							value={field.value as string}
							ref={field.ref}
							onChange={onChange}
							onBlur={field.onBlur}
							invalid={fieldState.invalid}
							disabled={disabled}
						/>
					);
				}

				return (
					<FormField
						label={props.label}
						description={props.editDescription}
						errorText={get(formState, `errors.${props.path}.message`)}
						info={props.infoHelpPanel && <HelpPanelInfoLink helpPanel={props.infoHelpPanel} />}
					>
						<span {...props.dataAttributes}>{CodeEditor}</span>
					</FormField>
				);
			}}
		/>
	);
}
